/**
 * Screen 8 — the QR ticket.
 *
 * Non-negotiable 4: this renders offline. The device's saved copy is read
 * FIRST and painted immediately; the network refresh is a bonus that
 * corrects the state if anything changed. Sangam Hall wifi will not survive
 * 300 people opening this at 6:55pm, and a spinner at the door is a failure.
 *
 * The QR is drawn locally by the bundled `qrcode` library — no image URL, no
 * request, nothing to fail when there is no signal.
 */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import QRCode from "qrcode";

import { Note, Shell } from "../components/Shell";
import type { Locale } from "../i18n";
import { api } from "../lib/api";
import { formatDateTime, formatIdr } from "../lib/format";
import { loadTicket, saveTicket, type StoredTicket } from "../lib/tickets";

export default function Ticket({ locale }: { locale: Locale }) {
  const { ref = "" } = useParams();
  const { t } = useTranslation();
  const canvas = useRef<HTMLCanvasElement>(null);

  // Painted from the device before any request is made.
  const [ticket, setTicket] = useState<StoredTicket | null>(() => loadTicket(ref));
  const [offline, setOffline] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .booking(ref)
      .then((booking) => {
        if (cancelled) return;
        setOffline(false);
        if (!booking.qr_payload) return;
        const stored: Omit<StoredTicket, "saved_at"> = {
          ref: booking.ref,
          state: booking.state,
          qr_payload: booking.qr_payload,
          seats: booking.seats.map((s) => s.label),
          total_idr: booking.total_idr,
          program: {
            id: booking.program.id,
            slug: booking.program.slug,
            date_time: booking.program.date_time,
            venue: booking.program.venue,
            name: (booking.program.text?.name as string) ?? "",
          },
        };
        saveTicket(stored);
        setTicket({ ...stored, saved_at: new Date().toISOString() });
      })
      .catch(() => {
        // No network. The saved copy already on screen is the ticket.
        if (!cancelled) setOffline(true);
      });
    return () => {
      cancelled = true;
    };
  }, [ref]);

  useEffect(() => {
    if (!ticket?.qr_payload || !canvas.current) return;
    void QRCode.toCanvas(canvas.current, ticket.qr_payload, {
      // Generous quiet zone and high error correction: this gets scanned off
      // a cracked screen at an angle, in a dim hall, by a volunteer holding
      // a phone in the other hand.
      errorCorrectionLevel: "H",
      margin: 3,
      width: 280,
      color: { dark: "#1c1c1a", light: "#ffffff" },
    });
  }, [ticket]);

  if (!ticket) {
    return (
      <Shell area="bookings" title={t("ticket.title")}>
        <Note>{offline ? t("error.network") : t("common.loading")}</Note>
      </Shell>
    );
  }

  return (
    <Shell area="bookings" title={t("ticket.title")}>
      {/* A stub, because that is what it is: the detail above the tear, the
          QR below it. */}
      <div className="stub">
        <p>
          <span className="state" data-state={ticket.state}>
            {t(`state.${ticket.state}`)}
          </span>
        </p>

        <h2 className="stub__programme">{ticket.program.name}</h2>

        <dl className="stub__rows">
          <dt>{t("program.date")}</dt>
          <dd>{formatDateTime(ticket.program.date_time, locale)}</dd>
          <dt>{t("program.venue")}</dt>
          <dd>{ticket.program.venue}</dd>
          <dt>{t("ticket.seat")}</dt>
          <dd>{ticket.seats.join(", ")}</dd>
          <dt>{t("checkout.total")}</dt>
          <dd className="money">{formatIdr(ticket.total_idr)}</dd>
        </dl>

        <div className="stub__perf" aria-hidden="true" />

        <div className="qr-plate">
          <canvas ref={canvas} aria-label={`${t("ticket.booking_ref")} ${ticket.ref}`} />
        </div>

        <p className="stub__ref">{ticket.ref}</p>
      </div>

      <Note>{t("ticket.offline_note")}</Note>
      {offline && <Note>{t("error.offline_ticket")}</Note>}
    </Shell>
  );
}
